import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Database, LogOut, Menu, Moon, Sun, X } from 'lucide-react';
import { Button } from './ui/Button.js';
import { cn } from '../lib/cn.js';
import { useAuth } from '../lib/auth.js';
import { useTheme } from '../lib/theme.js';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/learn', label: 'Learn' },
  { to: '/playground', label: 'Playground' },
  { to: '/interview', label: 'Interview' },
  { to: '/groups', label: 'Groups' },
  { to: '/history', label: 'History' },
];

/** Appended for admins only; the API rejects the calls behind it regardless. */
const ADMIN_NAV_ITEM = { to: '/admin', label: 'Admin' };

const linkClasses = (isActive: boolean, block: boolean) =>
  cn(
    'rounded-lg font-medium whitespace-nowrap transition-colors',
    block ? 'block px-3 py-2 text-base' : 'px-3 py-1.5 text-sm',
    isActive
      ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
  );

export function AppLayout() {
  const { user, logout, registrationOpen } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const items = [...NAV_ITEMS, ...(user?.role === 'admin' ? [ADMIN_NAV_ITEM] : [])];

  // Navigating closes the menu. Without this it stays open over the page the
  // learner just asked for.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  async function handleLogout() {
    await logout();
    void navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-slate-800 dark:bg-slate-900/80">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 sm:gap-6 sm:px-6">
          <Link to={user ? '/dashboard' : '/'} className="flex shrink-0 items-center gap-2">
            <Database className="size-5 text-brand-600 dark:text-brand-400" aria-hidden="true" />
            <span className="font-semibold text-slate-900 dark:text-slate-100">pgLearn</span>
          </Link>

          {/* Six destinations do not fit a phone. Rather than a horizontally
              scrolling strip, where half the items are simply invisible, the
              nav collapses into a disclosure below lg. */}
          {user && (
            <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
              {items.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) => linkClasses(isActive, false)}
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          )}

          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={toggle}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === 'dark' ? (
                <Sun className="size-4" aria-hidden="true" />
              ) : (
                <Moon className="size-4" aria-hidden="true" />
              )}
            </button>

            {user ? (
              <>
                <span className="hidden text-sm text-slate-600 sm:inline dark:text-slate-400">
                  {user.displayName}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  aria-label="Sign out"
                  className="hidden lg:inline-flex"
                >
                  <LogOut className="size-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Sign out</span>
                </Button>

                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-expanded={menuOpen}
                  aria-controls="mobile-nav"
                  aria-label={menuOpen ? 'Close the menu' : 'Open the menu'}
                  className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  {menuOpen ? (
                    <X className="size-5" aria-hidden="true" />
                  ) : (
                    <Menu className="size-5" aria-hidden="true" />
                  )}
                </button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Sign in
                  </Button>
                </Link>
                {registrationOpen !== false && (
                  <Link to="/register">
                    <Button size="sm">Get started</Button>
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {user && menuOpen && (
          <nav
            id="mobile-nav"
            aria-label="Main"
            className="border-t border-slate-200 px-3 py-2 lg:hidden dark:border-slate-800"
          >
            <ul className="space-y-0.5">
              {items.map(({ to, label }) => (
                <li key={to}>
                  <NavLink to={to} className={({ isActive }) => linkClasses(isActive, true)}>
                    {label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-1">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-base font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <LogOut className="size-4" aria-hidden="true" />
                  Sign out
                </button>
              </li>
            </ul>
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 py-6 dark:border-slate-800">
        <div className="mx-auto max-w-7xl px-4 text-sm text-slate-500 sm:px-6 dark:text-slate-400">
          Learn PostgreSQL by writing PostgreSQL.
        </div>
      </footer>
    </div>
  );
}
